"use client";

import { useRef, useState, useEffect } from "react";
import { DropdownItem, DropdownProps } from "./types";
import clsx from "clsx";
import DropdownChevron from "./DropdownChevron";
import useOutsideClick from "@/app/(site)/_hooks/useOutsideClick";

export default function Dropdown({ id, title, options, style, selectedOption, onSelect }: DropdownProps) {
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const [selectedItem, setSelectedItem] = useState<DropdownItem | undefined>(
		selectedOption ? options?.find((item: DropdownItem) => item.value === selectedOption) : undefined,
	);

	useEffect(() => {
		if (selectedOption && options) {
			const newSelectedItem = options.find((item: DropdownItem) => item.value === selectedOption);
			if (newSelectedItem) {
				setSelectedItem(newSelectedItem);
			}
		} else {
			setSelectedItem(undefined);
			setIsHovered(false);
		}
	}, [selectedOption, options]);

	useOutsideClick({
		ref: dropdownRef,
		handler: () => setIsOpen(false),
	});

	const handleChange = (item: DropdownItem) => {
		setSelectedItem(item);
		if (onSelect) {
			onSelect(item.value);
		}
		setIsOpen((prev) => !prev);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			setIsOpen((prev) => !prev);
		}
	};

	const dropdownClass = clsx("absolute left-0 bg-white w-full z-50 shadow-lg mt-1.5 rounded-lg border border-graphite-black/10 ");

	const optionTextClass = clsx("block truncate transition-all duration-300 ease text-body-m", {
		"text-graphite-black": (isOpen || isHovered) && !selectedItem,
		"text-navy-blue-black": selectedItem,
		"text-graphite-black/40": !isOpen && !isHovered && !selectedItem,
	});

	const arrowClass = clsx("transition-transform duration-300 ease", {
		"rotate-180": isOpen,
	});

	const buttonClass = clsx(
		"relative transition-all duration-300 ease flex py-4 w-full items-center justify-between pl-3 pr-5 text-body-m bg-middle-light rounded-lg",
		style,
		{
			"text-graphite-black/40": !isOpen && !isHovered && !selectedItem,
			"text-graphite-black": selectedItem,
			"text-graphite-black/40 ": (isOpen || isHovered) && !selectedItem,
		},
	);

	return (
		<div ref={dropdownRef} className="relative" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
			<button
				id={id}
				aria-label="Toggle dropdown"
				aria-haspopup="true"
				aria-expanded={isOpen}
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				onKeyDown={handleKeyDown}
				className={buttonClass}
			>
				<p className={optionTextClass}>{selectedItem?.label || title}</p>
				<div className={arrowClass}>
					<DropdownChevron />
				</div>
			</button>

			{isOpen && (
				<div aria-label="Dropdown menu" className={dropdownClass}>
					<ul role="menu" aria-labelledby={id} aria-orientation="vertical">
						{options.map((item: DropdownItem, index: number) => (
							<li
								key={item.value}
								onClick={() => handleChange(item)}
								className={clsx(
									"text-body-m hover:bg-middle-light/50 flex cursor-pointer items-center px-3 py-4 transition-colors duration-300",
									{
										"text-navy-blue-deep bg-middle-light/30 font-medium": selectedItem?.value === item.value,
										"text-graphite-black": selectedItem?.value !== item.value,
										"border-graphite-black/10 border-b": index !== options.length - 1,
									},
								)}
							>
								{item.label}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}
